import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon, List, ListItem, Searchbar } from 'framework7-react';

import { I18n } from 'Locales';
import { colors } from 'Theme';
import { ColumnView, RowView } from 'Containers';

import './style.scss';
import {
  calculateSubtotal,
  componentPropTypes,
  formatCurrency,
  getCartLines,
  getDiscountRules,
  resolveDiscount
} from '../../shared';

const compareByName = (a, b) => (a.name || '').localeCompare(b.name || '');

const serializeCartLines = lines =>
  JSON.stringify(lines.map(line => ({ product_id: line.product_id, quantity: line.quantity })));

const ProductCart = props => {
  const {
    componentFlowData,
    flowCountry,
    id,
    max_qty_per_product,
    max_unique_products,
    discount_rules: discountRulesRaw,
    name,
    referenced_products = [],
    required,
    shouldShowError,
    updateValue,
    value
  } = props;

  const discountRules = useMemo(() => getDiscountRules(discountRulesRaw), [discountRulesRaw]);

  const maxQty = parseInt(max_qty_per_product) || 99;
  const maxUniqueProducts = parseInt(max_unique_products) || Infinity;

  const existingLines = useMemo(() => getCartLines(componentFlowData, value, referenced_products), [
    componentFlowData,
    value,
    referenced_products
  ]);

  const [cartLines, setCartLines] = useState(existingLines);
  const [searchTerm, setSearchTerm] = useState('');
  const hasDispatchedInitialRef = useRef(false);

  useEffect(() => {
    if (existingLines.length > 0) {
      setCartLines(existingLines);
    }
  }, [existingLines]);

  const computeTotals = useCallback(
    lines => {
      const subtotal = calculateSubtotal(lines);
      const { discountAmount, matchedRule, negativeTotal } = resolveDiscount(discountRules, subtotal);
      return {
        subtotal,
        discountAmount,
        matchedRule,
        negativeTotal,
        finalTotal: Math.max(0, subtotal - discountAmount)
      };
    },
    [discountRules]
  );

  const { subtotal, discountAmount, matchedRule, negativeTotal, finalTotal } = useMemo(() => computeTotals(cartLines), [
    cartLines,
    computeTotals
  ]);

  const dispatchUpdate = useCallback(
    updatedLines => {
      const totals = computeTotals(updatedLines);

      updateValue({
        value: serializeCartLines(updatedLines),
        id,
        component: 'product_cart',
        lines: updatedLines,
        subtotal: totals.subtotal.toFixed(2),
        discount_amount: totals.discountAmount.toFixed(2),
        final_total: totals.finalTotal.toFixed(2),
        showInSummaryPage: true
      });
    },
    [computeTotals, id, updateValue]
  );

  useEffect(() => {
    if (existingLines.length > 0 && !hasDispatchedInitialRef.current) {
      hasDispatchedInitialRef.current = true;
      dispatchUpdate(existingLines);
    }
  }, [existingLines, dispatchUpdate]);

  const handleAdd = useCallback(
    product => {
      if (cartLines.length >= maxUniqueProducts) return;

      const newLine = {
        product_id: product.id,
        reference_number: product.reference_number,
        name: product.name,
        unit_price: parseFloat(product.value || 0),
        quantity: 1
      };
      const updatedLines = [...cartLines, newLine];
      setCartLines(updatedLines);
      dispatchUpdate(updatedLines);
    },
    [cartLines, maxUniqueProducts, dispatchUpdate]
  );

  const handleRemove = useCallback(
    productId => {
      const updatedLines = cartLines.filter(line => line.product_id !== productId);
      setCartLines(updatedLines);
      dispatchUpdate(updatedLines);
    },
    [cartLines, dispatchUpdate]
  );

  const handleQuantityChange = useCallback(
    (productId, newQty) => {
      const clampedQty = Math.max(1, Math.min(parseInt(newQty) || 1, maxQty));
      const updatedLines = cartLines.map(line =>
        line.product_id === productId ? { ...line, quantity: clampedQty } : line
      );
      setCartLines(updatedLines);
      dispatchUpdate(updatedLines);
    },
    [cartLines, maxQty, dispatchUpdate]
  );

  const filteredProducts = useMemo(() => {
    const products = referenced_products || [];
    const term = searchTerm.toLowerCase().trim();
    if (!term) return [...products].sort(compareByName);
    return products
      .filter(
        p => (p.name || '').toLowerCase().includes(term) || (p.reference_number || '').toLowerCase().includes(term)
      )
      .sort(compareByName);
  }, [referenced_products, searchTerm]);

  const selectedProductIds = useMemo(() => new Set(cartLines.map(line => line.product_id)), [cartLines]);
  const isLimitReached = cartLines.length >= maxUniqueProducts;

  const quantityOptions = useMemo(() => Array.from({ length: maxQty }, (_, i) => i + 1), [maxQty]);

  return (
    <ColumnView id="product-cart" alignItems="stretch" paddingHorizontal={16} paddingVertical={12} gap={4}>
      <ColumnView>
        <RowView justifyContent="flex-start">
          <div className={`item-title item-label ${shouldShowError ? 'warning' : ''}`}>{name}</div>
          {required && <div className="text-fail text-md pl-4">*</div>}
        </RowView>

        <Searchbar
          init={false}
          placeholder={I18n.t('flow:components.productCart.searchPlaceholder')}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onClickClear={() => setSearchTerm('')}
          onSubmit={e => e.preventDefault()}
        />
      </ColumnView>

      {isLimitReached && (
        <RowView
          justifyContent="flex-start"
          alignItems="center"
          flexWrap="wrap"
          gap={8}
          paddingBottom={8}
          className="product-cart__header-alerts">
          <div className="product-cart__limit-msg">
            {I18n.t('flow:components.productCart.maxProductsReached', { max: maxUniqueProducts })}
          </div>
        </RowView>
      )}

      <div className="product-cart__product-list">
        {filteredProducts.length === 0 ? (
          <div className="product-cart__empty">
            {searchTerm.trim().length > 0
              ? I18n.t('flow:components.productCart.noSearchResults')
              : I18n.t('flow:components.productCart.noProducts')}
          </div>
        ) : (
          <List noHairlines className="product-cart__list">
            {filteredProducts.map(product => {
              const isSelected = selectedProductIds.has(product.id);
              const isDisabled = isSelected || isLimitReached;
              return (
                <ListItem
                  key={product.id}
                  className="product-cart__item"
                  title={`${product.reference_number} ${product.name}`}
                  after={formatCurrency(product.value || 0, flowCountry)}>
                  <div slot="inner-end">
                    <RowView width="auto" marginLeft={8}>
                      <span
                        className={`product-cart__add-icon ${isDisabled ? 'product-cart__add-icon--disabled' : ''}`}
                        onClick={isDisabled ? undefined : () => handleAdd(product)}>
                        <Icon md={isSelected ? 'material:check' : 'material:add'} />
                      </span>
                    </RowView>
                  </div>
                </ListItem>
              );
            })}
          </List>
        )}
      </div>

      {cartLines.length > 0 ? (
        <div className="product-cart__selection">
          <RowView justifyContent="flex-start" alignItems="center" paddingBottom={4}>
            <span className="product-cart__selection-header">
              {I18n.t('flow:components.productCart.selectedProducts')} ({cartLines.length})
            </span>
          </RowView>

          <List noHairlines className="product-cart__cart-list">
            {cartLines.map(line => (
              <ListItem
                key={line.product_id}
                className="product-cart__cart-item"
                title={`${line.reference_number} ${line.name}`}
                after={formatCurrency(line.unit_price * line.quantity, flowCountry)}>
                <div slot="inner-end">
                  <RowView width="auto" gap={10} marginLeft={8}>
                    <select
                      className="product-cart__qty-select"
                      value={line.quantity}
                      onChange={e => handleQuantityChange(line.product_id, e.target.value)}>
                      {quantityOptions.map(n => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <span className="product-cart__remove-icon" onClick={() => handleRemove(line.product_id)}>
                      <Icon md="material:close" />
                    </span>
                  </RowView>
                </div>
              </ListItem>
            ))}
          </List>
        </div>
      ) : (
        <div className="product-cart__empty-cart">{I18n.t('flow:components.productCart.emptyCart')}</div>
      )}

      <ColumnView alignItems="stretch" paddingTop={10}>
        {negativeTotal && (
          <div className="product-cart__discount-warning">
            {I18n.t('flow:components.productCart.discountNotApplied')}
          </div>
        )}

        <RowView justifyContent="space-between" paddingVertical={4} className="product-cart__totals-row">
          <span>{I18n.t('flow:components.productCart.subtotal')}</span>
          <span>{formatCurrency(subtotal, flowCountry)}</span>
        </RowView>

        <RowView justifyContent="space-between" paddingVertical={4} className="product-cart__totals-row">
          <span>
            {I18n.t('flow:components.productCart.discount')}
            {matchedRule &&
              ` (${
                matchedRule.discount_kind === 'percent'
                  ? `${matchedRule.discount_value}%`
                  : formatCurrency(matchedRule.discount_value, flowCountry)
              })`}
          </span>
          <span>{formatCurrency(discountAmount, flowCountry)}</span>
        </RowView>

        <RowView backgroundColor={colors.darkGrey} height={1} />

        <RowView
          justifyContent="space-between"
          paddingTop={8}
          className="product-cart__totals-row product-cart__totals-row--final">
          <span>{I18n.t('flow:components.productCart.total')}</span>
          <span>{formatCurrency(finalTotal, flowCountry)}</span>
        </RowView>
      </ColumnView>

      {shouldShowError && <div className="product-cart__error-message">{I18n.t('flow:generalError')}</div>}
    </ColumnView>
  );
};

ProductCart.propTypes = {
  ...componentPropTypes,
  componentFlowData: PropTypes.object,
  discount_rules: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  flowCountry: PropTypes.string,
  max_qty_per_product: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max_unique_products: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  referenced_products: PropTypes.array,
  required: PropTypes.bool,
  value: PropTypes.any
};

export default ProductCart;
